
import { AltsAllocation } from "@/types/investment";
import { ALTS_COLORS } from "@/utils/colors";
import { useToast } from "@/hooks/use-toast";
import { AllocationModal } from "./AllocationModal";
import { CustomLegend } from "./CustomLegend";
import AltsChart from "./chart/AltsChart";
import AltsChartHeader from "./chart/AltsChartHeader";
import { useAllocationManager } from "./hooks/useAllocationManager";
import { transformAllocationsToChartData, calculateTotalAllocation } from "./utils/allocationUtils";

interface AltsPieChartProps {
  altAllocations: AltsAllocation | null;
  onAllocationsChange?: (newAllocations: Partial<AltsAllocation>) => void;
}

const AltsPieChart = ({ altAllocations, onAllocationsChange }: AltsPieChartProps) => {
  const { toast } = useToast();
  const {
    hiddenSectors,
    isEditing,
    hasBeenEdited,
    editableAllocations,
    setHasBeenEdited,
    setIsEditing,
    handleEditStart,
    updateHiddenSectors,
    handleAllocationChange
  } = useAllocationManager(altAllocations, onAllocationsChange);

  if (!altAllocations) return null;

  const allData = transformAllocationsToChartData(altAllocations);
  const totalAllocation = calculateTotalAllocation(allData, hiddenSectors);

  const colors = [
    ALTS_COLORS.spaceCadet,
    ALTS_COLORS.yaleBlue,
    ALTS_COLORS.yinMnBlue,
    ALTS_COLORS.paynesGray,
    ALTS_COLORS.battleshipGray,
    ALTS_COLORS.lion,
    ALTS_COLORS.satinSheenGold,
    ALTS_COLORS.goldenrod
  ];

  const chartData = allData.map(item => ({
    ...item,
    isHidden: hiddenSectors.includes(item.name)
  }));

  const handleSaveAllocations = () => {
    const newTotal = Object.values(editableAllocations).reduce((a, b) => a + b, 0);
    
    if (newTotal !== 100) {
      toast({
        title: "Invalid Allocation",
        description: "Total allocation must equal 100%",
        variant: "destructive"
      });
      return;
    }

    const newAllocations: Partial<AltsAllocation> = {
      private_equity_allocation: editableAllocations["Private Equity"],
      private_credit_allocation: editableAllocations["Private Credit"],
      private_debt_allocation: editableAllocations["Private Debt"],
      real_estate_allocation: editableAllocations["Real Estate"],
      hedge_funds_allocation: editableAllocations["Hedge Funds"],
      commodities_allocation: editableAllocations["Commodities"],
      cryptocurrencies_allocation: editableAllocations["Cryptocurrencies"],
      collectibles_allocation: editableAllocations["Collectibles"]
    };

    if (onAllocationsChange) {
      onAllocationsChange(newAllocations);
    }

    setHasBeenEdited(true);
    setIsEditing(false);
  };

  const handleLegendClick = (entry: any) => {
    const sector = entry.value;
    const sectorData = allData.find(item => item.name === sector);
    
    if (hasBeenEdited || (sectorData && (sectorData.value === 0 || hiddenSectors.includes(sector)))) {
      handleEditStart();
    } else {
      updateHiddenSectors(sector, allData);
    }
  };

  return (
    <div className="relative w-full max-w-3xl mx-auto">
      <AltsChartHeader 
        totalAllocation={totalAllocation} 
        onEditClick={handleEditStart}
      />
      
      <div className="flex flex-col items-center gap-4">
        <AltsChart chartData={chartData} colors={colors} />
        <CustomLegend 
          allData={allData}
          hiddenSectors={hiddenSectors}
          onLegendClick={handleLegendClick}
        />
      </div>

      <AllocationModal
        isOpen={isEditing}
        onOpenChange={setIsEditing}
        allData={allData}
        editableAllocations={editableAllocations}
        onAllocationChange={handleAllocationChange}
        onSave={handleSaveAllocations}
      />
    </div>
  );
};

export default AltsPieChart;
